import { useEffect, useState } from "react";
import i18n from "../../i18n";
import { hasAnyCanonicalRole } from "../../lib/auth";
import { fetchCurrencyOptions, fetchSystemCategories } from "../../services/leadManagementService";

/** The values the lead API accepts for these fields. */
export const LEAD_STATUSES = ["Hot", "Warm", "Cold", "On_Hold", "Won", "Lost", "Partially_Invoiced", "Fully_Invoiced"];
export const LEAD_SOURCES = ["Inbound_From_Customer", "Sales_Executive", "Networking", "Referral"];
export const PROJECT_TYPES = ["Trading", "SITC", "Service"];
export const LOST_REASONS = [
  "Price", "Competitor_Won", "Budget_Withdrawn", "Project_Cancelled",
  "No_Response", "Technical_Non-Compliance", "Others",
];
export const RELATIONSHIP_LEVELS = [1, 2, 3, 4, 5];
export const RELATIONSHIP_LEVEL_LABELS = {
  1: "1 - New Customer",
  2: "2 - Customer Knows Us",
  3: "3 - Lead Stage",
  4: "4 - Transaction Stage",
  5: "5 - Existing Customer",
};
export const ACTIVITY_STATUSES = ["Not_Started", "In_Progress", "On_Hold", "Completed"];

/* ------------------------------------------------------------- formatting */

/** A flat key in the leadManagement section: ("fields", "leadId") → "leadManagement.fieldsLeadId". */
export const leadKey = (group, name) => `leadManagement.${group}${name.charAt(0).toUpperCase()}${name.slice(1)}`;

/** A stored value (status, source, lost reason…) in the reader's language. */
export const valueLabel = (value) =>
  i18n.t(leadKey("values", String(value)), { defaultValue: String(value ?? "").replace(/_/g, " ") });

export const toOptions = (values) => values.map((value) => ({ value, label: valueLabel(value) }));

export const formatAmount = (amount, currency = "") =>
  amount === null || amount === undefined || amount === ""
    ? ""
    : `${currency} ${Number(amount).toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 2 })}`.trim();

export const formatDay = (value) =>
  value
    ? new Date(value).toLocaleDateString(i18n.resolvedLanguage, { day: "2-digit", month: "short", year: "numeric" })
    : "";

/** Terminal statuses: the lead locks read-only once it reaches one. */
export const LOCKED_STATUSES = ["Lost", "Fully_Invoiced"];
export const isLockedLead = (lead) => LOCKED_STATUSES.includes(lead?.leadStatus);

/** The canonical roles allowed to create a lead. */
export const CREATE_ROLES = ["Sales_Executive", "Sales_Support_Executive", "Sales_Manager"];
export const canCreateLead = () => hasAnyCanonicalRole(CREATE_ROLES);

/* ------------------------------------------------------------------ form */

/** Fields the Create Lead screen captures; Lead Information adds the rest. */
export const CREATE_FIELDS = [
  "leadStatus", "leadSource", "leadVertical", "customerId", "pointOfContactId",
  "contactEmail", "contactPhone", "isKeyAccount", "isExistingCustomer", "customerRelationshipLevel",
  "projectName", "projectLocation", "projectType", "systemsRequired",
  "projectValueAmount", "projectValueMetric",
];

export const INFORMATION_FIELDS = [
  "boqReceivedDate", "offerDueDate", "orderFinalizationByDate", "customerPaymentCredentials",
  "siteVisitRequired", "surveyRequired", "boqGenerated", "isDesignAndBuild",
];

const BOOLEAN_FIELDS = ["isKeyAccount", "isExistingCustomer", "siteVisitRequired", "surveyRequired", "boqGenerated", "isDesignAndBuild"];
const NUMBER_FIELDS = ["customerId", "pointOfContactId", "customerRelationshipLevel", "projectValueAmount", "projectValueMetric"];

const REQUIRED = [
  "leadSource", "leadVertical", "customerId",
  "projectName", "projectType", "systemsRequired", "projectValueAmount",
];

export const MAX_LENGTH = { projectName: 50, projectLocation: 50, leadVertical: 30 };

/** Form state from a lead (or empty for a new one). Systems Required is kept as a list. */
export function formFromLead(lead = {}) {
  return [...CREATE_FIELDS, ...INFORMATION_FIELDS].reduce((form, key) => {
    const value = lead[key];
    if (key === "systemsRequired") form[key] = value ? value.split(",") : [];
    else if (BOOLEAN_FIELDS.includes(key)) form[key] = value ?? null;
    else form[key] = value == null ? "" : String(value);
    return form;
  }, {});
}

/** The request body: numbers as numbers, unanswered fields left out. */
export function payloadFromForm(form, fields) {
  return fields.reduce((body, key) => {
    const value = form[key];
    if (value === "" || value === null || (Array.isArray(value) && !value.length)) return body;
    if (key === "systemsRequired") body[key] = value.join(",");
    else if (NUMBER_FIELDS.includes(key)) body[key] = Number(value);
    else body[key] = value;
    return body;
  }, {});
}

export function validateLeadForm(form, { requireStatus = false } = {}) {
  const errors = {};
  const required = requireStatus ? ["leadStatus", ...REQUIRED] : REQUIRED;
  required.forEach((key) => {
    if (!form[key]?.length) errors[key] = i18n.t("leadManagement.errorsRequired");
  });
  return errors;
}

/** Apply one change. A new customer or contact clears what belonged to the old one. */
export function nextLeadForm(form, key, value) {
  if (key === "customerId") {
    return { ...form, customerId: value, pointOfContactId: "", contactEmail: "", contactPhone: "" };
  }
  if (key === "pointOfContactId") {
    return { ...form, pointOfContactId: value, contactEmail: "", contactPhone: "" };
  }
  return { ...form, [key]: value };
}

/** Systems and currencies, each loaded on its own when the form opens. */
export function useLeadLookups() {
  const [lookups, setLookups] = useState({ systems: [], currencies: [], error: "" });

  useEffect(() => {
    [
      ["systems", fetchSystemCategories],
      ["currencies", fetchCurrencyOptions],
    ].forEach(([key, read]) =>
      read()
        .then((value) => setLookups((current) => ({ ...current, [key]: value })))
        .catch((error) => setLookups((current) => ({ ...current, error: error.message })))
    );
  }, []);

  return lookups;
}
